import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Col, Row } from 'reactstrap';

import { useIdentificationTypeLoader } from '../../../../../../../components/loaders/IdentificationTypeLoader';
import { PathsLayouts } from '../../../../../../../config/routes';
import { useParameters } from '../../../../../../../hooks/useParameters';
import { useValidations } from '../../../../../../../hooks/useValidations';
import { IdentificationDocumentCode } from '../../../../../../../utils/enums/identification-document-code.enum';
import { getSelectedOption } from '../../../../../../../utils/selectFormat';
import useGetIdentificationQuery from '../../hooks/useGetIdentificationQuery';

interface FormValues {
  identificationType: SelectOptionType | null;
  identification: string;
  country?: SelectOptionType | null;
}

interface SearchControlsProps {
  prefix: string;
}

const SearchControls = ({ prefix }: SearchControlsProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { countryOptions } = useParameters();
  const { validateRut } = useValidations();
  const { queryParams } = useGetIdentificationQuery();
  const { data } = useIdentificationTypeLoader();

  const identificationTypeOptions = useMemo(() => {
    return (data?.identificationTypes ?? []).map((item) => ({
      value: item.id,
      label: item.name,
      rules: item.rules,
    }));
  }, [data]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      identificationType: queryParams?.identificationTypeId
        ? getSelectedOption(
            queryParams.identificationTypeId,
            identificationTypeOptions,
          )
        : null,
      identification: queryParams?.identification ?? '',
      country: queryParams?.countryId
        ? getSelectedOption(queryParams.countryId, countryOptions)
        : null,
    },
    mode: 'onChange',
  });

  const identificationType = watch('identificationType');
  const identification = watch('identification');

  useEffect(() => {
    const cleaned = identification
      ?.replace('.', '')
      .replace('-', '')
      .toUpperCase()
      .trim();
    setValue('identification', cleaned);
  }, [setValue, identification]);

  useEffect(() => {
    if (identificationType?.value !== IdentificationDocumentCode.Passport) {
      setValue('country', null);
    }
  }, [setValue, identificationType]);

  const dynamicValidate = useCallback(
    (value: string | SelectOptionType | null | undefined) => {
      let payload = value ?? undefined;
      if (payload && typeof payload !== 'string') {
        payload = String(payload.value);
      }

      if (identificationType?.value === IdentificationDocumentCode.Passport) {
        return undefined;
      }
      if (identificationType?.value === IdentificationDocumentCode.CL_RUT) {
        return validateRut(payload);
      }

      if (identificationType?.rules && payload) {
        const rules = identificationType.rules as {
          replacement?: Record<string, string>;
          min?: number;
          max?: number;
        };
        if (rules.replacement) {
          const patterns = Object.values(rules.replacement);
          const charPattern =
            patterns.length === 1 ? patterns[0] : `(?:${patterns.join('|')})`;
          const min = rules.min ?? 1;
          const max = rules.max ?? 20;
          const regex = new RegExp(`^${charPattern}{${min},${max}}$`);
          return regex.test(payload) ? undefined : 'Formato inválido';
        }
      }
      return undefined;
    },
    [identificationType, validateRut],
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      const params = new URLSearchParams();
      params.set(
        'identificationTypeId',
        String(values.identificationType?.value ?? ''),
      );
      params.set('identification', values.identification);
      if (values.country?.value) {
        params.set('countryId', String(values.country.value));
      }
      history.replace(
        `${PathsLayouts.usersAndRoles}/users/create?${params.toString()}`,
      );
    },
    [history],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col className="pb-3" xs={12} lg={3} xl={2}>
          <OutlinedSelect
            label={t(`${prefix}.searchControls.identificationType`)}
            name="identificationType"
            options={identificationTypeOptions}
            isSearchable={false}
            control={control}
            rules={{ required: 'Campo requerido' }}
          />
        </Col>
        {identificationType?.value === IdentificationDocumentCode.Passport && (
          <Col className="pl-3" xs={12} lg={2} xl={2}>
            <OutlinedSelect
              label={t(`${prefix}.searchControls.country`)}
              name="country"
              isSearchable
              placeholder={t(`${prefix}.searchControls.inputCountry`)}
              control={control}
              options={countryOptions}
              rules={{ required: 'Campo requerido' }}
              shouldUnregister
            />
          </Col>
        )}
        <Col className="pl-3" xs={12} lg={4} xl={2}>
          <TextOutlinedInput
            label={t(`${prefix}.searchControls.identification`)}
            name="identification"
            control={control}
            rules={{
              validate: { require: (value) => dynamicValidate(value) },
            }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={2} xl={2}>
          <div className="d-flex justify-content-around pt-4">
            <Button
              type="submit"
              text={t('common.actions.search')}
              size="md"
              fullwidth
              disabled={
                Object.keys(formState.errors).length > 0 || !identification
              }
            />
            <Button
              type="button"
              text={t('common.actions.clean')}
              className="mt-0 ml-1"
              outlined
              onClick={() => {
                reset({
                  identificationType: null,
                  identification: '',
                  country: null,
                });
                history.replace(`${PathsLayouts.usersAndRoles}/users/create`);
              }}
              size="md"
              fullwidth
            />
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default SearchControls;
